/**
 * useHengenmi Hook
 *
 * 【変幻身】キーワード能力を管理するカスタムフック。
 * SP2を支払い、デッキ内の同属性モンスター（コスト±1）に変化する。
 *
 * 特徴:
 * - useState なし（ランダム選択なのでUIなし）
 * - HP割合・チャージ・バフを引き継ぎ、デバフは解消
 * - 変化後すぐ攻撃可能
 */

import { useCallback } from 'react';
import type React from 'react';
import {
  hasHengenmi,
  getHengenmiTargets,
  HENGENMI_SP_COST,
} from '../../engine/keywordAbilities';
import { createMonsterInstance } from '../../utils/helpers';
import { registerCardTriggers, unregisterCardTriggers } from '../../engine/triggerEngine';
import { continuousEffectEngine } from '../../engine/continuousEffects';
import type { MonsterInstance, CardWithId } from '../../types/card';
import type { PlayerNumber } from '../../types/player';

interface UseHengenmiProps {
  currentPlayer: PlayerNumber;
  phase: number;
  p1ActiveSP: number;
  p2ActiveSP: number;
  p1Field: (MonsterInstance | null)[];
  p2Field: (MonsterInstance | null)[];
  p1Deck: CardWithId[];
  p2Deck: CardWithId[];
  addLog: (message: string, type?: 'info' | 'damage' | 'heal') => void;
  setP1Field: React.Dispatch<React.SetStateAction<(MonsterInstance | null)[]>>;
  setP2Field: React.Dispatch<React.SetStateAction<(MonsterInstance | null)[]>>;
  setP1Deck: React.Dispatch<React.SetStateAction<CardWithId[]>>;
  setP2Deck: React.Dispatch<React.SetStateAction<CardWithId[]>>;
  setP1ActiveSP: React.Dispatch<React.SetStateAction<number>>;
  setP2ActiveSP: React.Dispatch<React.SetStateAction<number>>;
  setP1RestedSP: React.Dispatch<React.SetStateAction<number>>;
  setP2RestedSP: React.Dispatch<React.SetStateAction<number>>;
  setSelectedFieldMonster?: React.Dispatch<React.SetStateAction<number | null>>;
}

interface UseHengenmiReturn {
  activateHengenmi: (monsterIndex: number) => boolean;
}

export const useHengenmi = ({
  currentPlayer,
  phase,
  p1ActiveSP,
  p2ActiveSP,
  p1Field,
  p2Field,
  p1Deck,
  p2Deck,
  addLog,
  setP1Field,
  setP2Field,
  setP1Deck,
  setP2Deck,
  setP1ActiveSP,
  setP2ActiveSP,
  setP1RestedSP,
  setP2RestedSP,
  setSelectedFieldMonster,
}: UseHengenmiProps): UseHengenmiReturn => {
  /**
   * 【変幻身】発動
   */
  const activateHengenmi = useCallback((monsterIndex: number): boolean => {
    const myField = currentPlayer === 1 ? p1Field : p2Field;
    const myDeck = currentPlayer === 1 ? p1Deck : p2Deck;
    const activeSP = currentPlayer === 1 ? p1ActiveSP : p2ActiveSP;
    const setMyField = currentPlayer === 1 ? setP1Field : setP2Field;
    const setMyDeck = currentPlayer === 1 ? setP1Deck : setP2Deck;
    const setActiveSP = currentPlayer === 1 ? setP1ActiveSP : setP2ActiveSP;
    const setRestedSP = currentPlayer === 1 ? setP1RestedSP : setP2RestedSP;

    const monster = myField[monsterIndex];
    if (!monster) {
      addLog('【変幻身】対象モンスターが見つかりません', 'damage');
      return false;
    }

    if (!hasHengenmi(monster)) {
      addLog('【変幻身】このモンスターは変幻身を持っていません', 'damage');
      return false;
    }

    if (phase !== 2) {
      addLog('【変幻身】メインフェイズでのみ発動可能です', 'damage');
      return false;
    }

    if (activeSP < HENGENMI_SP_COST) {
      addLog(`【変幻身】SPが足りません（必要: ${HENGENMI_SP_COST}, 現在: ${activeSP}）`, 'damage');
      return false;
    }

    // 変化先候補を取得
    const targets = getHengenmiTargets(monster, myDeck);
    if (targets.length === 0) {
      addLog('【変幻身】デッキに変化先候補がありません', 'damage');
      return false;
    }

    // ランダムで変化先を選択
    const randomIndex = Math.floor(Math.random() * targets.length);
    const targetCard = targets[randomIndex] as CardWithId;

    // デッキ内でのインデックスを見つける
    const deckIndex = myDeck.findIndex(c => c.uniqueId === targetCard.uniqueId);
    if (deckIndex === -1) {
      addLog('【変幻身】変化先カードが見つかりません', 'damage');
      return false;
    }

    // 変化前モンスターの情報を保存
    const oldMonster = monster;
    const baseHp = oldMonster.hp ?? 1000; // デフォルト値
    const oldCurrentHp = oldMonster.currentHp ?? baseHp;
    const oldMaxHp = oldMonster.maxHp ?? baseHp;
    const oldHpRatio = oldCurrentHp / oldMaxHp;
    const oldCharges = oldMonster.charges || [];
    const oldCanAttack = oldMonster.canAttack;

    // バフを抽出（デバフは除外）
    // ATK上昇系のバフ
    const baseAtk = oldMonster.attack ?? 1000; // デフォルト値
    const atkBuff = (oldMonster.currentAttack ?? baseAtk) - baseAtk;
    const atkBuffPositive = Math.max(0, atkBuff); // 正のバフのみ

    // リンクボーナス（魂結）
    const linkedTo = oldMonster.linkedTo;
    const linkedBonus = oldMonster.linkedBonus;
    const linkedEndPhaseDamage = oldMonster.linkedEndPhaseDamage;

    // その他のバフ状態
    const damageBoost = oldMonster.damageBoost;
    const canDoubleAttack = oldMonster.canDoubleAttack;
    const attackCount = oldMonster.attackCount;

    // 変化前のトリガー・常時効果を解除
    unregisterCardTriggers(oldMonster.uniqueId);
    continuousEffectEngine.unregister(oldMonster.uniqueId);

    // 新しいモンスターインスタンスを作成
    const newMonster = createMonsterInstance(targetCard);
    newMonster.owner = currentPlayer;

    // HP割合を引き継ぎ
    const newMaxHp = newMonster.hp ?? 1000;
    const newCurrentHp = Math.max(1, Math.floor(newMaxHp * oldHpRatio));
    newMonster.maxHp = newMaxHp;
    newMonster.currentHp = newCurrentHp;

    // チャージを引き継ぎ
    newMonster.charges = [...oldCharges];

    // バフを引き継ぎ（ATK上昇）
    if (atkBuffPositive > 0) {
      const newBaseAtk = newMonster.attack ?? 1000;
      newMonster.currentAttack = (newMonster.currentAttack ?? newBaseAtk) + atkBuffPositive;
    }

    // リンク状態を引き継ぎ
    if (linkedTo) {
      newMonster.linkedTo = linkedTo;
      if (linkedBonus) {
        newMonster.linkedBonus = linkedBonus;
      }
      if (linkedEndPhaseDamage) {
        newMonster.linkedEndPhaseDamage = linkedEndPhaseDamage;
      }
    }

    // その他のバフを引き継ぎ
    if (damageBoost) newMonster.damageBoost = damageBoost;
    if (canDoubleAttack) newMonster.canDoubleAttack = canDoubleAttack;
    if (attackCount !== undefined) newMonster.attackCount = attackCount;

    // 変化後すぐ攻撃可能（元の状態を引き継ぎ）
    newMonster.canAttack = oldCanAttack;

    // デバフは解消（statusEffectsをクリア）
    newMonster.statusEffects = [];

    // デッキからカードを除去
    setMyDeck(prev => {
      const newDeck = [...prev];
      newDeck.splice(deckIndex, 1);
      return newDeck;
    });

    // フィールドを更新
    setMyField(prev => {
      const newField = [...prev];
      newField[monsterIndex] = newMonster;
      return newField;
    });

    // SPを消費
    setActiveSP(prev => Math.max(0, prev - HENGENMI_SP_COST));
    setRestedSP(prev => prev + HENGENMI_SP_COST);

    // トリガー登録
    registerCardTriggers(newMonster, currentPlayer, monsterIndex);

    // 常時効果登録
    continuousEffectEngine.register(newMonster, currentPlayer);

    addLog(`【変幻身】${oldMonster.name}が${newMonster.name}に変化！（HP: ${newCurrentHp}/${newMaxHp}）`, 'info');

    // 選択状態をクリア
    if (setSelectedFieldMonster) {
      setSelectedFieldMonster(null);
    }

    return true;
  }, [
    currentPlayer, phase,
    p1ActiveSP, p2ActiveSP, p1Field, p2Field, p1Deck, p2Deck,
    addLog,
    setP1Field, setP2Field, setP1Deck, setP2Deck,
    setP1ActiveSP, setP2ActiveSP, setP1RestedSP, setP2RestedSP,
    setSelectedFieldMonster,
  ]);

  return {
    activateHengenmi,
  };
};

export default useHengenmi;
